import type { HexColor } from '@getaccept/fried-tofu';
import { hexToHsl, hexToRgb, hslToHex } from '../../helpers';
import type { EntityTheme } from '../types/theme';

export type ContrastData = {
  contrastRatio: number;
  isNormalTextAA: boolean;
  isNormalTextAAA: boolean;
  isLargeTextAA: boolean;
  isLargeTextAAA: boolean;
  isAccessible: boolean;
};

export const WCAG_AA = 4.5 as const;
export const WCAG_AAA = 7 as const;
export const CONTRAST_BACKGROUND_COLOR = '#FFFFFF' as const;

export function getLuminance(r: number, g: number, b: number) {
  const [rs, gs, bs] = [r, g, b].map(c => {
    c = c / 255;
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

export function getContrastRatio(l1: number, l2: number) {
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

export function getAccessibleTextColor(backgroundColor: string) {
  const [r, g, b] = hexToRgb(backgroundColor);
  const bgLuminance = getLuminance(r, g, b);

  const whiteContrast = getContrastRatio(1, bgLuminance);
  const blackContrast = getContrastRatio(0, bgLuminance);

  if (whiteContrast >= WCAG_AA && whiteContrast > blackContrast) {
    return '#FFFFFF';
  } else if (blackContrast >= WCAG_AA) {
    return '#2C3B4E';
  }
  return bgLuminance > 0.5 ? '#2C3B4E' : '#FFFFFF';
}

export function calculateSecondaryColor(colorOne: HexColor): HexColor {
  const colorOneHSL = hexToHsl(colorOne);

  if (colorOneHSL.l > 80) {
    return hslToHex(colorOneHSL.h, colorOneHSL.s, Math.max(colorOneHSL.l - 20, 0));
  }

  if (colorOneHSL.l < 20) {
    return hslToHex(colorOneHSL.h, colorOneHSL.s, Math.min(colorOneHSL.l + 20, 100));
  }

  const colorTwoL = Math.min(Math.max(colorOneHSL.l + 10, 0), 100);
  return hslToHex(colorOneHSL.h, colorOneHSL.s, colorTwoL);
}

export function lightenColor(color: HexColor, percentage: number): HexColor {
  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);

  const lightenFactor = percentage / 100;

  const lightenedR = Math.round(r + (255 - r) * lightenFactor);
  const lightenedG = Math.round(g + (255 - g) * lightenFactor);
  const lightenedB = Math.round(b + (255 - b) * lightenFactor);

  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(lightenedR)}${toHex(lightenedG)}${toHex(lightenedB)}`;
}

export function darkenColor(color: HexColor, percentage: number): HexColor {
  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);

  const darkenFactor = percentage / 100;

  const darkenedR = Math.round(r * (1 - darkenFactor));
  const darkenedG = Math.round(g * (1 - darkenFactor));
  const darkenedB = Math.round(b * (1 - darkenFactor));

  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(darkenedR)}${toHex(darkenedG)}${toHex(darkenedB)}`;
}

export function calculateOverlayColor(lightness: number) {
  const color = lightness < 0 ? 'rgba(0, 0, 0,' : 'rgba(255, 255, 255,';
  const opacity = Math.abs(lightness) / 50;
  return `${color}${opacity})`;
}

export function calculateLinearGradient(primaryColor: HexColor, secondaryColor: HexColor) {
  return `linear-gradient(to top right, ${primaryColor}, ${secondaryColor})`;
}

export function calculateRadialGradient(primaryColor: HexColor, secondaryColor: HexColor) {
  return `radial-gradient(${primaryColor}, ${secondaryColor})`;
}

export function calculateConicGradient(primaryColor: HexColor, secondaryColor: HexColor) {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const angleToTopRight = Math.atan2(-viewportHeight, viewportWidth) * (180 / Math.PI);
  const fromAngle = 90 + angleToTopRight;

  return `conic-gradient(from ${fromAngle}deg, ${primaryColor}, ${secondaryColor})`;
}

export function calculateSolidLinearGradientWithOverlay(
  primaryColor: HexColor,
  overlayColor: string
) {
  return `linear-gradient(${overlayColor}, ${overlayColor}), ${primaryColor}`;
}

export function calculateLinearGradientWithOverlay(linearGradient: string, overlayColor: string) {
  return `linear-gradient(${overlayColor}, ${overlayColor}), ${linearGradient}`;
}

export function calculateRadialGradientWithOverlay(radialGradient: string, overlayColor: string) {
  return `linear-gradient(${overlayColor}, ${overlayColor}), ${radialGradient}`;
}

export function calculateConicGradientWithOverlay(conicGradient: string, overlayColor: string) {
  return `linear-gradient(${overlayColor}, ${overlayColor}), ${conicGradient}`;
}

export function checkContrastRatio(
  color1?: HexColor | 'transparent',
  color2?: HexColor
): ContrastData | undefined {
  if (!color1 || !color2 || color1 === 'transparent') return undefined;
  const [r1, g1, b1] = hexToRgb(color1);
  const [r2, g2, b2] = hexToRgb(color2);

  const l1 = getLuminance(r1, g1, b1);
  const l2 = getLuminance(r2, g2, b2);

  const contrastRatio = getContrastRatio(l1, l2);
  const normalTextAAThreshold = WCAG_AA;
  const normalTextAAAThreshold = WCAG_AAA;
  const largeTextAAThreshold = 3;
  const largeTextAAAThreshold = WCAG_AA;

  return {
    contrastRatio: Number(contrastRatio.toFixed(2)),
    isNormalTextAA: contrastRatio >= normalTextAAThreshold,
    isNormalTextAAA: contrastRatio >= normalTextAAAThreshold,
    isLargeTextAA: contrastRatio >= largeTextAAThreshold,
    isLargeTextAAA: contrastRatio >= largeTextAAAThreshold,
    isAccessible: contrastRatio >= normalTextAAThreshold,
  };
}

export function doesThemePassContrastCheck(theme: EntityTheme) {
  const { color, h1Color, h2Color, textColor } = theme;

  return (
    checkContrastRatio(CONTRAST_BACKGROUND_COLOR, color)?.isNormalTextAA &&
    checkContrastRatio(CONTRAST_BACKGROUND_COLOR, h1Color)?.isNormalTextAA &&
    checkContrastRatio(CONTRAST_BACKGROUND_COLOR, h2Color)?.isNormalTextAA &&
    checkContrastRatio(CONTRAST_BACKGROUND_COLOR, textColor)?.isNormalTextAA
  );
}
